"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.applyStatementsToStore = applyStatementsToStore;
exports.applyUpdaterPatch = applyUpdaterPatch;
exports.collectLinkStatements = collectLinkStatements;
exports.collectLinkedNodeStatements = collectLinkedNodeStatements;
exports.collectNodeStatements = collectNodeStatements;
exports.ensureStandardMutationPrefixes = ensureStandardMutationPrefixes;
exports.findExistingNode = findExistingNode;
exports.isMissingGetRecordErrorMessage = isMissingGetRecordErrorMessage;
exports.isPatchFailureMessage = isPatchFailureMessage;
exports.putResourceWithStatements = putResourceWithStatements;
exports.registerStorePrefix = registerStorePrefix;
exports.replacePredicateStatements = replacePredicateStatements;
exports.runDirectUpdaterUpdate = runDirectUpdaterUpdate;
exports.runUpdateTransport = runUpdateTransport;
exports.sanitizePatchStatements = sanitizePatchStatements;
exports.shouldForceDocumentPutForStatements = shouldForceDocumentPutForStatements;
exports.statementKey = statementKey;
exports.uniqueStatements = uniqueStatements;
var _rdflib = require("rdflib");
var _texts = require("../../texts");
/* Some of the code below was generated by AI Model: GPT-5.4.
   Prompt: can you add types to rdfMutationHelpers.ts and consolidate mutation logic 
   where appropriate. */

const STANDARD_MUTATION_PREFIXES = {
  rdf: 'http://www.w3.org/1999/02/22-rdf-syntax-ns#',
  vcard: 'http://www.w3.org/2006/vcard/ns#',
  foaf: 'http://xmlns.com/foaf/0.1/',
  solid: 'http://www.w3.org/ns/solid/terms#',
  schema: 'http://schema.org/',
  org: 'http://www.w3.org/ns/org#',
  owl: 'http://www.w3.org/2002/07/owl#',
  dc: 'http://purl.org/dc/elements/1.1/'
};
const STANDARD_MUTATION_NAMESPACE_URIS = Object.values(STANDARD_MUTATION_PREFIXES);
function normalizeNodeId(value) {
  return value.startsWith('_:') ? value.slice(2) : value;
}
function isCompleteStatement(statement) {
  return Boolean(statement?.subject && statement?.predicate && statement?.object);
}
function statementTermKey(term) {
  if (!term) return '';
  if (typeof term.toNT === 'function') return term.toNT();
  return term.value || '';
}
function registerStorePrefix(target, prefix, uri) {
  if (!target) return;
  if (typeof target.setPrefixForURI === 'function') {
    target.setPrefixForURI(prefix, uri);
    return;
  }
  if (!target.namespaces) {
    target.namespaces = {};
  }
  target.namespaces[prefix] = uri;
}
function ensureStandardMutationPrefixes(store) {
  if (!store) return;
  const updater = getStoreUpdater(store);
  const targets = [store, updater, updater?.store];
  Object.entries(STANDARD_MUTATION_PREFIXES).forEach(([prefix, uri]) => {
    targets.forEach(target => {
      registerStorePrefix(target, prefix, uri);
    });
  });
}
function statementKey(statement) {
  return `${statementTermKey(statement.subject)} ${statementTermKey(statement.predicate)} ${statementTermKey(statement.object)} ${statementTermKey(statement.why)}`;
}
function uniqueStatements(statements) {
  return Array.from(new Map((statements || []).map(statement => [statementKey(statement), statement])).values());
}
function applyStatementsToStore(store, deletions, insertions) {
  store.remove(deletions);
  insertions.forEach(statement => {
    store.add(statement.subject, statement.predicate, statement.object, statement.why);
  });
}
function sanitizePatchStatements(store, deletions, insertions, requireStoredDelete = false) {
  const safeDeletions = uniqueStatements((deletions || []).filter(statement => {
    if (!isCompleteStatement(statement)) return false;
    if (store.holds(statement.subject, statement.predicate, statement.object, statement.why)) return true;
    return requireStoredDelete ? false : store.statementsMatching(statement.subject, statement.predicate, statement.object, statement.why).length > 0;
  }));
  const safeInsertions = uniqueStatements((insertions || []).filter(isCompleteStatement));
  return {
    safeDeletions,
    safeInsertions
  };
}
function getStoreUpdater(store) {
  return store.updater;
}
function getStoreFetcher(store) {
  return store.fetcher;
}
async function bestEffortLoadDocIfStoreEmpty(store, doc) {
  if (!doc) return;
  const fetcher = getStoreFetcher(store);
  if (typeof fetcher?.load !== 'function') return;
  if (store.statementsMatching(undefined, undefined, undefined, doc).length > 0) return;
  try {
    await fetcher.load(doc);
  } catch {
    // Continue with current in-memory statements if the pre-load fails.
  }
}
function escapeRegex(text) {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
function statementNamespaceUris(statement, candidateNamespaceUris) {
  const uris = new Set();
  const terms = [statement.subject, statement.predicate, statement.object];
  terms.forEach(term => {
    if (!term) return;
    if (typeof term.value === 'string') {
      candidateNamespaceUris.forEach(namespaceUri => {
        if (term.value.startsWith(namespaceUri)) {
          uris.add(namespaceUri);
        }
      });
    }
    if (term.termType === 'Literal' && typeof term.datatype?.value === 'string') {
      candidateNamespaceUris.forEach(namespaceUri => {
        if (term.datatype.value.startsWith(namespaceUri)) {
          uris.add(namespaceUri);
        }
      });
    }
  });
  return [...uris];
}
function documentDeclaresNamespacePrefix(documentText, namespaceUri) {
  const escapedNamespaceUri = escapeRegex(namespaceUri);
  const prefixPattern = new RegExp(`(^|\\n)\\s*(?:@prefix|PREFIX)\\s+[A-Za-z][\\w-]*:\\s*<${escapedNamespaceUri}>`, 'i');
  return prefixPattern.test(documentText);
}
async function shouldForceDocumentPutForStatements(store, doc, statements, candidateNamespaceUris = STANDARD_MUTATION_NAMESPACE_URIS, options = {}) {
  const updater = getStoreUpdater(store);
  const fetcher = getStoreFetcher(store);
  if (typeof updater?.serialize !== 'function' || typeof fetcher?.webOperation !== 'function') {
    return false;
  }
  const neededNamespaceUris = Array.from(new Set((statements || []).flatMap(statement => statementNamespaceUris(statement, candidateNamespaceUris))));
  if (neededNamespaceUris.length === 0) {
    return false;
  }
  const cachedDocumentText = options.documentTextCache?.get(doc.value);
  const existingDocumentText = typeof options.documentText === 'string' ? options.documentText : cachedDocumentText;
  if (typeof existingDocumentText === 'string') {
    return neededNamespaceUris.some(namespaceUri => {
      return !documentDeclaresNamespacePrefix(existingDocumentText, namespaceUri);
    });
  }
  try {
    const response = await fetcher.webOperation('GET', doc.value, {
      noMeta: true,
      headers: {
        accept: 'text/turtle'
      }
    });
    if (!response?.ok || typeof response.responseText !== 'string') {
      return false;
    }
    options.documentTextCache?.set(doc.value, response.responseText);
    return neededNamespaceUris.some(namespaceUri => {
      return !documentDeclaresNamespacePrefix(response.responseText || '', namespaceUri);
    });
  } catch {
    return false;
  }
}
function isPatchFailureMessage(message) {
  const text = (message || '').toLowerCase();
  return text.includes('fetch error for patch') || text.includes('failed to fetch') || text.includes(' on patch ') || text.includes('web error: 400') || text.includes('web error: 405') || text.includes('web error: 500') || text.includes('web error: 501');
}
function isMissingGetRecordErrorMessage(message) {
  return (message || '').toLowerCase().includes('no record of our http get request for document');
}
function runUpdaterUpdate(updater, deletions, insertions, failureMessage) {
  if (typeof updater.update !== 'function') {
    throw new Error(_texts.updaterUnsupportedStoreErrorMessageText);
  }
  return new Promise((resolve, reject) => {
    try {
      updater.update(deletions, insertions, (_uri, ok, message) => {
        if (ok === true) {
          resolve();
          return;
        }
        reject(new Error(message || failureMessage));
      });
    } catch (error) {
      reject(error instanceof Error ? error : new Error(String(error)));
    }
  });
}
async function runDirectUpdaterUpdate(store, deletions, insertions, failureMessage = _texts.fallbackSaveUpdatesErrorMessageText) {
  ensureStandardMutationPrefixes(store);
  const updater = getStoreUpdater(store);
  if (!updater) {
    throw new Error(_texts.updaterUnsupportedStoreErrorMessageText);
  }
  await runUpdaterUpdate(updater, deletions, insertions, failureMessage);
}
async function putResourceWithStatements(store, doc, deletions, insertions, unsupportedMessage, documentTextCache) {
  ensureStandardMutationPrefixes(store);
  const updater = getStoreUpdater(store);
  const fetcher = getStoreFetcher(store);
  if (!updater || typeof updater.serialize !== 'function' || !fetcher || typeof fetcher.webOperation !== 'function') {
    throw new Error(unsupportedMessage);
  }
  await bestEffortLoadDocIfStoreEmpty(store, doc);
  const currentStatements = store.statementsMatching(undefined, undefined, undefined, doc).slice();
  const deletionKeys = new Set((deletions || []).map(statement => statementKey(statement)));
  const nextStatements = currentStatements.filter(statement => !deletionKeys.has(statementKey(statement))).concat(insertions || []);
  const serializationStore = (0, _rdflib.graph)();
  nextStatements.forEach(statement => {
    serializationStore.add(statement.subject, statement.predicate, statement.object, statement.why);
  });
  const contentType = 'text/turtle';
  const body = updater.serialize(doc.value, serializationStore.statementsMatching(undefined, undefined, undefined, doc), contentType);
  const response = await fetcher.webOperation('PUT', doc.value, {
    noMeta: true,
    contentType,
    body
  });
  if (!response || response.ok !== true) {
    const status = response?.status || 'unknown';
    throw new Error(`Web error: ${status} on PUT of <${doc.value}>`);
  }
  documentTextCache?.set(doc.value, body);
  applyStatementsToStore(store, deletions, insertions);
}
async function runUpdateTransport(store, doc, deletions, insertions, options) {
  ensureStandardMutationPrefixes(store);
  const updater = getStoreUpdater(store);
  if (!updater) {
    throw new Error(options.unsupportedMessage);
  }
  if (options.forcePut) {
    await bestEffortLoadDocIfStoreEmpty(store, doc);
  }
  const {
    safeDeletions,
    safeInsertions
  } = sanitizePatchStatements(store, deletions, insertions, options.requireStoredDelete === true);
  if (safeDeletions.length === 0 && safeInsertions.length === 0) {
    return;
  }
  if (options.forcePut) {
    if (!doc) {
      throw new Error(options.unsupportedMessage);
    }
    await putResourceWithStatements(store, doc, safeDeletions, safeInsertions, options.unsupportedMessage, options.documentTextCache);
    return;
  }
  const failureMessage = options.failureMessage || _texts.fallbackSaveUpdatesErrorMessageText;
  const isPatchFailure = options.patchFailureMatcher || isPatchFailureMessage;
  const isMissingGetRecord = options.missingGetRecordMatcher || isMissingGetRecordErrorMessage;
  try {
    await runUpdaterUpdate(updater, safeDeletions, safeInsertions, failureMessage);
    return;
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (!isPatchFailure(message)) {
      throw error;
    }
    if (options.useDavFallback && typeof updater.updateDav === 'function') {
      if (doc && getStoreFetcher(store)?.load) {
        try {
          await getStoreFetcher(store)?.load?.(doc);
        } catch {
          // Continue to DAV fallback and then PUT fallback if needed.
        }
      }
      try {
        await new Promise((resolve, reject) => {
          updater.updateDav?.(doc, safeDeletions, safeInsertions, (_uri, ok, body) => {
            if (ok === true) {
              resolve();
              return;
            }
            reject(new Error(body || message || failureMessage));
          });
        });
        return;
      } catch (davError) {
        const davMessage = davError instanceof Error ? davError.message : String(davError);
        if (!options.usePutFallback || !isMissingGetRecord(davMessage)) {
          throw davError;
        }
      }
    }
    if (options.usePutFallback) {
      if (!doc) {
        throw new Error(options.unsupportedMessage);
      }
      await putResourceWithStatements(store, doc, safeDeletions, safeInsertions, options.unsupportedMessage, options.documentTextCache);
      return;
    }
    throw error;
  }
}
function applyUpdaterPatch(store, deletions, insertions) {
  if (!store.updater) {
    throw new Error(_texts.updaterUnsupportedStoreErrorMessageText);
  }
  return runUpdateTransport(store, null, deletions, insertions, {
    unsupportedMessage: _texts.updaterUnsupportedStoreErrorMessageText,
    failureMessage: _texts.fallbackSaveUpdatesErrorMessageText,
    requireStoredDelete: true,
    useDavFallback: false,
    usePutFallback: false
  });
}
function collectNodeStatements(store, node, doc) {
  return store.statementsMatching(node, null, null, doc);
}
function collectLinkStatements(store, subject, predicate, node, doc) {
  return store.statementsMatching(subject, predicate, node, doc);
}

/* The following helper was generated by AI Model: GPT-5.3-Codex.
   Prompt: 'do you see anything I can do differently in the mutation code so that it's not so verbose and possible reusable accross sections' */
function replacePredicateStatements(store, subject, predicate, doc, deletions, insertions, nextObject) {
  deletions.push(...store.statementsMatching(subject, predicate, null, doc));
  if (nextObject) {
    insertions.push((0, _rdflib.st)(subject, predicate, nextObject, doc));
  }
}

/* The following helper was generated by AI Model: GPT-5.3-Codex.
   Prompt: 'do you see anything I can do differently in the mutation code so that it's not so verbose and possible reusable accross sections' */
function collectLinkedNodeStatements(store, sourceNode, predicate, doc) {
  const linkedNodes = store.each(sourceNode, predicate, null, doc);
  const linkedStatements = linkedNodes.flatMap(linkedNode => collectNodeStatements(store, linkedNode, doc));
  const linkStatements = store.statementsMatching(sourceNode, predicate, null, doc);
  return {
    linkedNodes,
    linkedStatements,
    linkStatements
  };
}
function findExistingNode(nodes, entryNode) {
  const normalizedEntryNode = normalizeNodeId(entryNode);
  return nodes.find(node => {
    const nodeValue = normalizeNodeId(node.value);
    if (nodeValue === normalizedEntryNode) return true;
    return node.value === entryNode;
  });
}